import { CAST_AI } from '@cast/constants';
import {
  Button,
  DialogCard,
  DialogFooterV2,
  DialogHeaderV2,
  Inset,
  SwipeableViews,
  makeDialog,
  useCloseDialog,
  Stack,
} from '@cast/design-system';
import { useStartTrialMutation } from '@cast/shared-trial-data-access';

import { FeedbackMessageSlide } from '../_components/FeedbackMessageSlide';
import { TrialFeaturesComparison } from '../_components/TrialFeaturesComparison/TrialFeaturesComparison';

type InitializeTrialSlideProps = {
  onConfirm: () => void;
  isLoading: boolean;
};

const InitializeTrialSlide = ({
  onConfirm,
  isLoading,
}: InitializeTrialSlideProps) => {
  return (
    <Stack data-testid="initialize-trial-slide">
      <DialogHeaderV2 title={`Try ${CAST_AI} for free today`} />

      <Inset mb={24}>
        <TrialFeaturesComparison />
      </Inset>

      <DialogFooterV2
        endContent={
          <Button
            testId="start-trial-button"
            onClick={onConfirm}
            loading={isLoading}
          >
            Start trial
          </Button>
        }
        divider
      />
    </Stack>
  );
};

const InitializeTrialDialog = () => {
  const {
    isSuccess,
    isIdle,
    isPending,
    mutate: startTrial,
  } = useStartTrialMutation();
  const closeDialog = useCloseDialog<boolean>();

  return (
    <DialogCard
      stopClickPropagation
      testId="initialize-trial-dialog"
      result={isSuccess}
    >
      <SwipeableViews
        slides={{
          main: (
            <InitializeTrialSlide
              onConfirm={() => startTrial()}
              isLoading={isPending}
            />
          ),
          feedback: (
            <FeedbackMessageSlide
              success={isSuccess}
              onContinue={() => closeDialog(isSuccess)}
            />
          ),
        }}
        activeSlide={isIdle || isPending ? 'main' : 'feedback'}
      />
    </DialogCard>
  );
};

export const useInitializeTrialDialog = makeDialog<undefined, boolean>(
  InitializeTrialDialog
);
